import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  FindForSyncProductOption,
  FindProposalItemForWholesaleCartBody,
  FindProposalItemsForCreateWholesaleDigitalMenuQuery,
  FindProposalItemsWithIsResetFlagQuery,
  MultiUpdateProposalItemPriceOrQuantityBody,
  ProposalItemApi,
  UpdateProposalItemCustomerQuantityBody,
  UpdateProposalItemPriceOrQuantityBody,
} from 'api';

export const findProposalItem = createAsyncThunk(
  'proposalItem/findProposalItem',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await ProposalItemApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProposalItemForSyncProduct = createAsyncThunk(
  'proposalItem/findProposalItemForSyncProduct',
  async (option: FindForSyncProductOption, { rejectWithValue }) => {
    try {
      const response = await ProposalItemApi.findForSyncProduct(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProposalItemsForCreateWholesaleDigitalMenu = createAsyncThunk(
  'proposalItem/findProposalItemsForCreateWholesaleDigitalMenu',
  async (
    option: FindProposalItemsForCreateWholesaleDigitalMenuQuery,
    { rejectWithValue }
  ) => {
    try {
      const response =
        await ProposalItemApi.findProposalItemsForCreateWholesaleDigitalMenu(
          option
        );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProposalItemsWithIsResetFlag = createAsyncThunk(
  'proposalItem/findProposalItemsWithIsResetFlag',
  async (
    option: FindProposalItemsWithIsResetFlagQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await ProposalItemApi.findWithIsResetFlag(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProposalItemsWithIsResetFlagV2 = createAsyncThunk(
  'proposalItem/findProposalItemsWithIsResetFlagV2',
  async (
    option: FindProposalItemsWithIsResetFlagQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await ProposalItemApi.findWithIsResetFlagV2(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findFreeProposalItemsForDigitalMenu = createAsyncThunk(
  'proposalItem/findFreeProposalItemsForDigitalMenu',
  async (
    params: { option: any; digitalMenuCategoryID: string },
    { rejectWithValue }
  ) => {
    const { option, digitalMenuCategoryID } = params;
    try {
      const response =
        await ProposalItemApi.findFreeProposalItemsForDigitalMenu(
          digitalMenuCategoryID,
          option
        );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProposalItemForWholesaleCart = createAsyncThunk(
  'proposalItem/findProposalItemForWholesaleCart',
  async (option: FindProposalItemForWholesaleCartBody, { rejectWithValue }) => {
    try {
      const response = await ProposalItemApi.findProposalItemForWholesaleCart(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const getProposalItemCount = createAsyncThunk(
  'proposalItem/getProposalItemCount',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await ProposalItemApi.getCount(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createProposalItem = createAsyncThunk(
  'proposalItem/createProposalItem',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await ProposalItemApi.create(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createProposalItemQuickly = createAsyncThunk(
  'proposalItem/createProposalItemQuickly',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await ProposalItemApi.createQuickly(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProposalItem = createAsyncThunk(
  'proposalItem/updateProposalItem',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProposalItemApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProposalItemCustomerQuantity = createAsyncThunk(
  'proposalItem/updateProposalItemCustomerQuantity',
  async (
    { id, body }: { id: string; body: UpdateProposalItemCustomerQuantityBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await ProposalItemApi.updateCustomerQuantityV2(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProposalItemCustomerQuantityOlder = createAsyncThunk(
  'proposalItem/updateProposalItemCustomerQuantityOlder',
  async (
    { id, body }: { id: string; body: UpdateProposalItemCustomerQuantityBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await ProposalItemApi.updateCustomerQuantity(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProposalItemNote = createAsyncThunk(
  'proposalItem/updateProposalItemNote',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProposalItemApi.updateNote(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProposalItemPriceOrQuantity = createAsyncThunk(
  'proposalItem/updateProposalItemPriceOrQuantity',
  async (
    { id, body }: { id: string; body: UpdateProposalItemPriceOrQuantityBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await ProposalItemApi.updatePriceOrQuantity(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const multiUpdateProposalItemPriceOrQuantity = createAsyncThunk(
  'proposalItem/multiUpdateProposalItemPriceOrQuantity',
  async (
    { body }: { body: MultiUpdateProposalItemPriceOrQuantityBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await ProposalItemApi.multiUpdatePriceOrQuantity(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeProposalItem = createAsyncThunk(
  'proposalItem/removeProposalItem',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await ProposalItemApi.remove(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProposalItemStatus = createAsyncThunk(
  'proposalItem/updateProposalItemStatus',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProposalItemApi.updateStatus(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface State {
  list: Array<any>;
  cartList: Array<any>;
  count: number;
  reloadFlag: boolean;
}

const initialState: State = {
  list: [],
  cartList: [],
  count: 0,
  reloadFlag: true,
};

const proposalItemSlice = createSlice({
  name: 'proposalItem',
  initialState,
  reducers: {
    updateProposalItemCount(state, action) {
      const { quantity } = action.payload;
      state.count += quantity;
    },
    reloadProposalItem(state) {
      state.reloadFlag = !state.reloadFlag;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(findProposalItem.fulfilled, (state, action) => {
        const { data } = action.payload;

        state.list = data;
      })
      .addCase(
        findProposalItemsForCreateWholesaleDigitalMenu.fulfilled,
        (state, action) => {
          const { data } = action.payload;

          state.list = data;
        }
      )
      .addCase(getProposalItemCount.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.count = data.size;
      })
      .addCase(findProposalItemForWholesaleCart.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
            customerProductID: item.customerProductID,
            entityID: item.supplier.id,
            entityLocation: item.supplier.ward,
          };
        });
        state.cartList = newData;
        state.count = newData.length;
      });
  },
});

const { actions, reducer: proposalItemReducer } = proposalItemSlice;

export const { updateProposalItemCount, reloadProposalItem } = actions;

export default proposalItemReducer;
